import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faGraduationCap,
    faScaleBalanced,
    faUsers,
    faAward,
    faBullseye,
    faHandshake,
    faGlobe,
    faLightbulb,
    faShieldAlt,
    faChartLine,
    faRocket,
    faHeadset,
    faClock,
    faGavel,
    faCertificate,
    faStar,
    faTrophy,
    faChevronRight,
    faCheckCircle,
    faBuilding,
    faUserTie,
    faChartBar,
    faBalanceScale,
    faQuoteLeft
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: "easeOut" }
    }
};



const reasons = [
    {
        icon: faGraduationCap,
        title: "London-Educated Expertise",
        description: "Both founding partners hold LL.M degrees from prestigious London universities (LSE & UCL), bringing international legal perspectives to Indian jurisprudence.",
        color: "gold",
        features: ["International Legal Training", "Global Best Practices", "Cross-border Expertise"],
        stat: ""
    },
    {
        icon: faScaleBalanced,
        title: "Advocate-on-Record Status",
        description: "Our partners are certified Advocates-on-Record of the Patna High Court, authorized to practice before the Supreme Court and all High Courts.",
        color: "primary-blue",
        features: ["Supreme Court Practice", "High Court Authority", "Direct Filing Rights"],
        stat: ""
    },
    {
        icon: faUsers,
        title: "18+ Years Combined ",
        description: "With over 36 years of collective experience, we've successfully handled thousands of cases across multiple legal domains.",
        color: "gold",
        features: ["Extensive Case History", "Diverse Legal Expertise", "Proven Track Record"],
        stat: ""
    },
    {
        icon: faAward,
        title: "Fortune 500 Clientele",
        description: "Trusted by 18+ Fortune 500 companies including Tata Projects, HDFC Bank, Hero MotoCorp, and Indigo Airlines.",
        color: "primary-blue",
        features: ["Corporate Trust", "Major Corporations", "Industry Leaders"],
        stat: ""
    },
    {
        icon: faBullseye,
        title: "Good Success Rate",
        description: "Consistently achieving favorable outcomes for our clients with a remarkable success rate in litigation and dispute resolution.",
        color: "gold",
        features: ["High Win Rate", "Strategic Litigation", "Effective Solutions"],
        stat: " "
    },
    {
        icon: faHandshake,
        title: "Client-Centric Approach",
        description: "Personalized attention, transparent communication, and dedicated case management for every client.",
        color: "primary-blue",
        features: ["Personalized Service", "Regular Updates", "Accessible Counsel"],
        stat: " "
    }
];

const values = [
    {
        icon: faShieldAlt,
        title: "Integrity & Ethics",
        description: "We uphold the highest standards of professional ethics and integrity in all our engagements.",
        color: "gold"
    },
    {
        icon: faLightbulb,
        title: "Innovative Solutions",
        description: "Creative legal strategies tailored to each client's unique challenges and objectives.",
        color: "primary-blue"
    },
    {
        icon: faChartLine,
        title: "Result-Oriented",
        description: "Focus on achieving practical, effective outcomes that serve our clients' best interests.",
        color: "gold"
    },
    {
        icon: faGlobe,
        title: "Global Perspective",
        description: "Combining international legal education with deep understanding of Indian laws and courts.",
        color: "primary-blue"
    }
];

const processSteps = [
    {
        number: "01",
        title: "Initial Consultation",
        description: "Comprehensive case analysis and strategy discussion",
        icon: faHeadset
    },
    {
        number: "02",
        title: "Case Assessment",
        description: "Detailed legal research and merit evaluation",
        icon: faLightbulb
    },
    {
        number: "03",
        title: "Strategy Development",
        description: "Customized legal strategy and action plan",
        icon: faChartLine
    },
    {
        number: "04",
        title: "Execution & Representation",
        description: "Court appearances, filings, and diligent representation",
        icon: faGavel
    },
    {
        number: "05",
        title: "Regular Updates",
        description: "Continuous communication and progress reports",
        icon: faClock
    },
    {
        number: "06",
        title: "Resolution & Follow-up",
        description: "Case closure documentation and future guidance",
        icon: faCertificate
    }
];

const testimonials = [
    {
        name: "Tata Projects Ltd",
        text: "RNA Law Chambers demonstrated exceptional expertise in handling our complex infrastructure contracts.",
        position: "Legal Department"
    },
    {
        name: "HDFC Bank Ltd",
        text: "Their banking law expertise and attention to detail in regulatory compliance is remarkable.",
        position: "Compliance Head"
    },
    {
        name: "Indigo Airlines",
        text: "International aviation laws handled with precision and strategic foresight.",
        position: "Legal Counsel"
    }
];

const HoverCard = ({ children, className = "", style = {}, ...props }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
        <motion.div
            className={`position-relative overflow-hidden ${className}`}
            style={style}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            {...props}
        >
            {/* Hover glow effect */}
            <motion.div
                className="position-absolute top-0 left-0 w-full h-full"
                initial={false}
                animate={{
                    opacity: isHovered ? 0.1 : 0,
                    scale: isHovered ? 1.05 : 1
                }}
                transition={{ duration: 0.3 }}
                style={{
                    background: "radial-gradient(circle at center, var(--gold) 0%, transparent 70%)",
                    zIndex: 0
                }}
            />

            {/* Border animation */}
            <motion.div
                className="position-absolute top-0 left-0 w-full h-full"
                initial={false}
                animate={{
                    opacity: isHovered ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                style={{
                    border: "2px solid var(--gold)",
                    borderRadius: "inherit",
                    zIndex: 1,
                    pointerEvents: "none"
                }}
            />

            {children}
        </motion.div>
    );
};



export const WhyChooseUs = () => {
    const [activeReason, setActiveReason] = useState(0);
    const  navigate= useNavigate();
    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Background decorative elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                {/* Animated gradient orbs */}
                <motion.div
                    className="position-absolute"
                    style={{
                        width: "400px",
                        height: "400px",
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.03,
                        borderRadius: "50%",
                        top: "10%",
                        left: "5%"
                    }}
                    animate={{
                        x: [0, 50, 0],
                        y: [0, 30, 0]
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />

                <motion.div
                    className="position-absolute"
                    style={{
                        width: "300px",
                        height: "300px",
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.03,
                        borderRadius: "50%",
                        bottom: "20%",
                        right: "10%"
                    }}
                    animate={{
                        x: [0, -40, 0],
                        y: [0, -20, 0]
                    }}
                    transition={{
                        duration: 12,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 1
                    }}
                />
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div variants={fadeInUp}>
                        <span style={{
                            color: "var(--gold)",
                            fontWeight: "600",
                            letterSpacing: "3px",
                            fontSize: "0.9rem",
                            textTransform: "uppercase",
                            display: "inline-block",
                            padding: "8px 20px",
                            border: "1px solid var(--gold-light)",
                            borderRadius: "50px",
                            background: "rgba(184, 134, 11, 0.05)",
                            backdropFilter: "blur(4px)"
                        }}>
                            The RNA Advantage
                        </span>
                        <motion.h4
                        className="fw-bold display-6 display-md-5 mt-2"
                        variants={fadeInUp}
                        style={{
                            fontFamily: "'Playfair Display', serif",
                            fontWeight: 600,
                            color: 'var(--primary-dark)',
                            background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text"
                        }}
                    >
                        Why Choose RNA Law Chambers
                    </motion.h4>
                    </motion.div>

                    <motion.p
                       className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                       variants={fadeInUp}
                       style={{
                           maxWidth: "750px",
                           color: "var(--text-primary)",
                           lineHeight: 1.7
                       }}
                    >
                        Discover why leading corporations and individuals trust RNA Law Chambers for
                        their most critical legal matters. Our unique combination of international
                        education, local expertise, and proven results sets us apart.
                    </motion.p>
                </motion.div>

                {/* Key Reasons Section */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faStar} className="me-3" style={{ color: "var(--gold)" }} />
                            Key Differentiators
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            What makes us the preferred choice for complex legal matters
                        </p>
                    </div>

                    <div className="row g-4">
                        {reasons.map((reason, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-4"
                                variants={itemVariants}
                                onMouseEnter={() => setActiveReason(index)}
                            >
                                <HoverCard
                                    className="h-100 p-4 rounded-4"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `1px solid var(--${reason.color}-light)`,
                                        boxShadow: "var(--shadow-soft)"
                                    }}
                                >
                                    <div className="position-relative z-2 h-100 d-flex flex-column">
                                        {/* Icon & Stat */}
                                        <div className="d-flex align-items-center justify-content-between mb-4">
                                            <motion.div
                                                className="p-3 rounded-3"
                                                style={{
                                                    backgroundColor: `rgba(var(--${reason.color}-rgb), 0.1)`,
                                                    color: `var(--${reason.color})`
                                                }}
                                                whileHover={{ rotate: 360 }}
                                                transition={{ duration: 0.6, ease: "easeInOut" }}
                                            >
                                                <FontAwesomeIcon icon={reason.icon} style={{ fontSize: "1.5rem" }} />
                                            </motion.div>

                                            <motion.div
                                                className="display-6 fw-bold"
                                                style={{
                                                    color: `var(--${reason.color})`,
                                                    fontFamily: "'Playfair Display', serif"
                                                }}
                                                animate={{
                                                    scale: activeReason === index ? 1.1 : 1
                                                }}
                                                transition={{ type: "spring", stiffness: 300 }}
                                            >
                                                {reason.stat}
                                            </motion.div>
                                        </div>

                                        {/* Title & Description */}
                                        <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                            {reason.title}
                                        </h5>

                                        <p className="mb-4 flex-grow-1" style={{
                                            color: "var(--text-primary)",
                                            lineHeight: 1.6,
                                            fontSize: "0.95rem"
                                        }}>
                                            {reason.description}
                                        </p>

                                        {/* Features */}
                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: `var(--${reason.color}-light)` }}
                                        >
                                            <div className="vstack gap-2">
                                                {reason.features.map((feature, idx) => (
                                                    <motion.div
                                                        key={idx}
                                                        className="d-flex align-items-center gap-2"
                                                        initial={{ opacity: 0, x: -10 }}
                                                        whileInView={{ opacity: 1, x: 0 }}
                                                        viewport={{ once: true }}
                                                        transition={{ delay: idx * 0.1 }}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            style={{
                                                                color: `var(--${reason.color})`,
                                                                fontSize: "0.8rem"
                                                            }}
                                                        />
                                                        <span className="small" style={{ color: "var(--text-primary)" }}>
                                                            {feature}
                                                        </span>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </HoverCard>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Our Values Section */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faTrophy} className="me-3" style={{ color: "var(--gold)" }} />
                            Our Core Values
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            The principles that guide our practice and define our approach
                        </p>
                    </div>

                    <div className="row g-4">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                className="col-md-6 col-lg-3"
                                variants={itemVariants}
                            >
                                <motion.div
                                    className="p-4 rounded-4 h-100 text-center"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: `2px solid var(--${value.color}-light)`,
                                        boxShadow: "var(--shadow-soft)"
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        borderColor: `var(--${value.color})`
                                    }}
                                >
                                    <motion.div
                                        className="mb-3"
                                        animate={{
                                            y: [0, -5, 0]
                                        }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            ease: "easeInOut"
                                        }}
                                    >
                                        <div className="p-3 rounded-circle d-inline-block"
                                            style={{
                                                backgroundColor: `rgba(var(--${value.color}-rgb), 0.1)`,
                                                color: `var(--${value.color})`
                                            }}
                                        >
                                            <FontAwesomeIcon icon={value.icon} style={{ fontSize: "1.8rem" }} />
                                        </div>
                                    </motion.div>

                                    <h5 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                                        {value.title}
                                    </h5>

                                    <p className="mb-0" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.6,
                                        fontSize: "0.9rem"
                                    }}>
                                        {value.description}
                                    </p>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Our Process Section */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faRocket} className="me-3" style={{ color: "var(--gold)" }} />
                            Our Proven Process
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            A systematic approach to achieving successful legal outcomes
                        </p>
                    </div>

                    <div className="position-relative">
                        {/* Connecting line */}
                        <div className="position-absolute top-1/2 left-0 w-100 h-1 d-none d-lg-block"
                            style={{
                                backgroundColor: "var(--gold-light)",
                                opacity: 0.3,
                                transform: "translateY(-50%)",
                                zIndex: 0
                            }}
                        ></div>

                        <div className="row g-4 position-relative z-1">
                            {processSteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    className="col-md-6 col-lg-4 col-xl-2"
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <div className="text-center">
                                        {/* Step Number */}
                                        <motion.div
                                            className="position-relative mb-3 mx-auto"
                                            style={{
                                                width: "70px",
                                                height: "70px"
                                            }}
                                            whileHover={{ rotate: 360 }}
                                            transition={{ duration: 0.6, ease: "easeInOut" }}
                                        >
                                            <div className="position-absolute top-0 start-0 w-100 h-100 rounded-circle"
                                                style={{
                                                    backgroundColor: "rgba(var(--gold-rgb), 0.1)",
                                                    border: "2px solid var(--gold-light)"
                                                }}
                                            ></div>

                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                                                <span className="fw-bold" style={{
                                                    color: "var(--gold)",
                                                    fontSize: "1.2rem"
                                                }}>
                                                    {step.number}
                                                </span>
                                            </div>
                                        </motion.div>

                                        {/* Step Icon */}
                                        <div className="mb-3">
                                            <div className="p-3 rounded-3 d-inline-block"
                                                style={{
                                                    backgroundColor: "rgba(var(--primary-blue-rgb), 0.1)",
                                                    color: "var(--primary-blue)"
                                                }}
                                            >
                                                <FontAwesomeIcon icon={step.icon} style={{ fontSize: "1.5rem" }} />
                                            </div>
                                        </div>

                                        {/* Step Content */}
                                        <div>
                                            <h6 className="fw-bold mb-2" style={{ color: "var(--primary-dark)" }}>
                                                {step.title}
                                            </h6>
                                            <p className="small mb-0" style={{
                                                color: "var(--text-primary)",
                                                lineHeight: 1.5
                                            }}>
                                                {step.description}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>

                {/* Client Testimonials */}
                {/* <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                        >
                            <FontAwesomeIcon icon={faBuilding} className="me-3" style={{ color: "var(--gold)" }} />
                            Trusted by Industry Leaders
                        </h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            What our Fortune 500 clients say about working with us
                        </p>
                    </div>

                    <div className="row g-4">
                        {testimonials.map((testimonial, index) => (
                            <motion.div
                                key={index}
                                className="col-md-4"
                                variants={itemVariants}
                            >
                                <HoverCard
                                    className="p-4 rounded-4 h-100"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)"
                                    }}
                                >
                                    <div className="position-relative z-2 h-100 d-flex flex-column">
                                        <FontAwesomeIcon
                                            icon={faQuoteLeft}
                                            className="mb-3"
                                            style={{
                                                color: "var(--gold-light)",
                                                fontSize: "1.8rem",
                                                opacity: 0.3
                                            }}
                                        />

                                        <p className="mb-4 flex-grow-1" style={{
                                            color: "var(--text-primary)",
                                            lineHeight: 1.7,
                                            fontStyle: "italic"
                                        }}>
                                            "{testimonial.text}"
                                        </p>

                                        <div className="mt-auto pt-3 border-top"
                                            style={{ borderColor: "var(--gold-light)" }}
                                        >
                                            <div className="fw-bold mb-1" style={{ color: "var(--primary-dark)" }}>
                                                {testimonial.name}
                                            </div>
                                            <div className="small" style={{ color: "var(--gold)" }}>
                                                {testimonial.position}
                                            </div>
                                        </div>
                                    </div>
                                </HoverCard>
                            </motion.div>
                        ))}
                    </div>
                </motion.div> */}

                {/* Comparative Advantage */}
                <motion.div
                    className="px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <div className="p-4 p-md-5 rounded-4"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)",
                            boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                        }}
                    >
                        <div className="row align-items-center">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        The RNA Difference
                                    </h3>
                                    <div className="row g-4 mb-4">
                                        {[
                                            { label: "International Education", value: "100%", icon: faGraduationCap },
                                            { label: "AOR Status", value: "100%", icon: faScaleBalanced },
                                            { label: "Good Success Rate", value: "96%", icon: faChartBar },
                                            { label: "Fortune 500 Clients", value: "18+", icon: faUserTie }
                                        ].map((stat, index) => (
                                            <motion.div
                                                key={index}
                                                className="col-6 col-md-3"
                                                variants={itemVariants}
                                            >
                                                <div className="text-center">
                                                    <div className="mb-2">
                                                        <FontAwesomeIcon
                                                            icon={stat.icon}
                                                            style={{
                                                                color: "var(--gold)",
                                                                fontSize: "1.5rem"
                                                            }}
                                                        />
                                                    </div>
                                                    {/* <div className="display-6 fw-bold mb-1" style={{ color: "var(--white)" }}>
                                                        {stat.value}
                                                    </div> */}
                                                    <div className="small" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    <p className="mb-0" style={{
                                        color: "rgba(255, 255, 255, 0.9)",
                                        lineHeight: 1.7
                                    }}>
                                        While many firms offer legal services, RNA Law Chambers provides the unique
                                        combination of London education, Advocate-on-Record status, extensive
                                        corporate experience, and a proven track record of success.
                                    </p>
                                </motion.div>
                            </div>

                            <div className="col-lg-4 text-center">
                                <motion.div
                                    className="p-4 rounded-4 d-inline-block"
                                    style={{
                                        backgroundColor: "rgba(184, 134, 11, 0.15)",
                                        border: "2px solid var(--gold)",
                                        backdropFilter: "blur(10px)"
                                    }}
                                    whileHover={{
                                        scale: 1.05,
                                        rotate: 5
                                    }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <FontAwesomeIcon
                                        icon={faBalanceScale}
                                        className="mb-3"
                                        style={{ fontSize: "3rem", color: "var(--gold)" }}
                                    />
                                    <div className="fw-bold mb-2" style={{ color: "var(--white)", fontSize: "1.2rem" }}>
                                        Comprehensive Legal Excellence
                                    </div>
                                    <div style={{ color: "var(--gold)" }}>
                                        Your Trusted Legal Partner
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* CTA Section */}
                <motion.div
                    className="text-center mt-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={containerVariants}
                >
                    <motion.div
                        className="p-5 rounded-4 mx-auto"
                        style={{
                            backgroundColor: "var(--white)",
                            border: "2px solid var(--gold-light)",
                            boxShadow: "var(--shadow-medium)",
                            maxWidth: "800px"
                        }}
                        variants={fadeInUp}
                    >
                        <FontAwesomeIcon
                            icon={faHandshake}
                            className="mb-4"
                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                        />
                        <h2 className="fw-bold mb-3" style={{ color: "var(--primary-dark)" }}>
                            Ready to Experience the RNA Advantage?
                        </h2>
                        <p className="mb-4 mx-auto" style={{
                            color: "var(--text-primary)",
                            maxWidth: "600px",
                            lineHeight: 1.7
                        }}>
                            Contact us today for a confidential consultation and discover how our unique
                            expertise can help you achieve your legal objectives.
                        </p>
                        <motion.button
                            className="btn px-5 py-3 fw-bold"
                            style={{
                                backgroundColor: "var(--gold)",
                                color: "var(--white)",
                                fontSize: "1rem",
                                border: "none",
                                borderRadius: "8px"
                            }}
                            whileHover={{
                                scale: 1.05,
                                backgroundColor: "var(--gold-light)"
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => navigate("/contact")}
                        >
                            Schedule Your Consultation
                            <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                        </motion.button>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};